import { ComponentType, ReactNode } from 'react';

import { FormElement, FormElementKind } from '@gearbelt/global';

import CalendarBlankIcon from '../icons/calendar-blank';
import FilePdfIcon from '../icons/file-pdf';
import GaugeIcon from '../icons/gauge';
import ListIcon from '../icons/list';
import MapIcon from '../icons/map';
import PhotoIcon from '../icons/photo';
import SignatureIcon from '../icons/signature';
import TableIcon from '../icons/table';
import TextIcon from '../icons/text';
import WarningIcon from '../icons/warning';
import YesNoIcon from '../icons/yes-no';
import { DateFormElementConfig, DateFormElementMock } from './elements/date';
import { DropdownFormElementConfig, DropdownFormElementMock } from './elements/dropdown';
import { IssuesSelectFormElementConfig, IssuesSelectFormElementMock } from './elements/issue-select';
import { LocationSelectFormElementConfig, LocationSelectFormElementMock } from './elements/location-select';
import { MultiLineInputFormElementConfig, MultiLineInputFormElementMock } from './elements/multi-line-input';
import { PassFailCheckFormElementConfig, PassFailCheckFormElementMock } from './elements/pass-fail-check';
import { PdfAttachmentFormElementConfig, PdfAttachmentFormElementMock } from './elements/pdf-attachment';
import { PhotoFormElementConfig, PhotoFormElementMock } from './elements/photo';
import { PreFilledTextFormElementConfig, PreFilledTextFormElementMock } from './elements/pre-filled-text';
import { SignatureArrayFormElementConfig, SignatureArrayFormElementMock } from './elements/signature-array';
import { SingleLineInputFormElementConfig, SingleLineInputFormElementMock } from './elements/single-line-input';
import { TableFormElementConfig, TableFormElementMock } from './elements/table';
import { UnitReadingFormElementConfig, UnitReadingFormElementMock } from './elements/unit-reading';
import type { FormElementConfig } from './form-designer.types';

type BaseFormDesignerElementProps = {
  kind: FormElementKind;
  label: string;
  description?: string;
};

type FormDesignerElementConfigurator = ComponentType<{
  element: FormElement;
  onChange?: (updates: Partial<Omit<FormElement, 'kind'>>) => void;
}>;

type FormDesignerElementMock = ComponentType<{ element: FormElement }>;

export type FormDesignerElementDefinition = BaseFormDesignerElementProps & {
  icon: ReactNode;
  category: string;
  mockElement: FormDesignerElementMock;
  configurator?: FormDesignerElementConfigurator;
  configuratorDefault?: FormElementConfig<FormElementKind>;
  isUnique?: boolean;
  isDeprecated?: boolean;
  disabledTooltipContent?: ReactNode;
  isDisabled?: boolean;
  isNotCloneable?: boolean;
};

export const FormDesignerElements: FormDesignerElementDefinition[] = [
  {
    label: `Unit Reading`,
    icon: <GaugeIcon />,
    category: 'Unit',
    description: '',
    mockElement: UnitReadingFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.UnitReading,
    configurator: UnitReadingFormElementConfig as FormDesignerElementConfigurator,
    isNotCloneable: true,
  },
  {
    label: 'Single line text',
    icon: <TextIcon />,
    category: 'Text',
    description: '',
    mockElement: SingleLineInputFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.SingleLineInput,
    configurator: SingleLineInputFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Multi line text',
    icon: <TextIcon />,
    category: 'Text',
    description: '',
    mockElement: MultiLineInputFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.MultiLineInput,
    configurator: MultiLineInputFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Prefilled text',
    icon: <TextIcon />,
    category: 'Text',
    description: 'Type text ...',
    mockElement: PreFilledTextFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.PreFilledText,
    configurator: PreFilledTextFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Single date time',
    icon: <CalendarBlankIcon />,
    category: 'Date - Timezone Aware',
    description: '',
    mockElement: DateFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.Date,
    configurator: DateFormElementConfig as FormDesignerElementConfigurator,
    configuratorDefault: {
      showDate: true,
      showTime: true,
      isSubmissionDate: false,
    },
  },
  {
    label: 'Single date',
    icon: <CalendarBlankIcon />,
    category: 'Date - Timezone Aware',
    description: '',
    mockElement: DateFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.Date,
    configurator: DateFormElementConfig as FormDesignerElementConfigurator,
    configuratorDefault: {
      showDate: true,
      showTime: false,
      isSubmissionDate: false,
    },
  },
  {
    label: 'Pass/Fail',
    icon: <YesNoIcon />,
    category: 'Pass/Fail',
    description: '',
    mockElement: PassFailCheckFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.PassFailCheck,
    configurator: PassFailCheckFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Pass/Fail/NA',
    icon: <YesNoIcon />,
    category: 'Pass/Fail',
    description: '',
    mockElement: PassFailCheckFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.PassFailWithNaCheck,
    configurator: PassFailCheckFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Dropdown',
    icon: <ListIcon />,
    category: 'List',
    description: '',
    mockElement: DropdownFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.Dropdown,
    configurator: DropdownFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Location List',
    icon: <MapIcon />,
    category: 'List',
    description: '',
    mockElement: LocationSelectFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.LocationSelect,
    configurator: LocationSelectFormElementConfig as FormDesignerElementConfigurator,
    isUnique: true,
  },
  {
    label: 'Default table',
    icon: <TableIcon />,
    category: 'Table',
    description: '',
    mockElement: TableFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.Table,
    configurator: TableFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Sign manually',
    icon: <SignatureIcon />,
    category: 'Signature',
    description: '',
    mockElement: SignatureArrayFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.SignatureArray,
    configurator: SignatureArrayFormElementConfig as FormDesignerElementConfigurator,
    configuratorDefault: {
      isManualSignature: true,
    },
  },
  {
    label: 'Signature',
    icon: <SignatureIcon />,
    category: 'Signature',
    description: '',
    mockElement: SignatureArrayFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.SignatureArray,
    configurator: SignatureArrayFormElementConfig as FormDesignerElementConfigurator,
    configuratorDefault: {
      isManualSignature: false,
    },
  },
  {
    label: 'Photos',
    icon: <PhotoIcon />,
    category: 'Photos',
    description: '',
    mockElement: PhotoFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.Photo,
    configurator: PhotoFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'PDF attachment',
    icon: <FilePdfIcon />,
    category: 'Media',
    description: '',
    mockElement: PdfAttachmentFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.PdfAttachment,
    configurator: PdfAttachmentFormElementConfig as FormDesignerElementConfigurator,
  },
  {
    label: 'Issue link',
    icon: <WarningIcon />,
    category: 'Issues',
    description: '',
    mockElement: IssuesSelectFormElementMock as FormDesignerElementMock,
    kind: FormElementKind.IssueSelect,
    configurator: IssuesSelectFormElementConfig as FormDesignerElementConfigurator,
    isUnique: true,
  },
];
